import {
  CopyIcon,
  Icon,
  type IconDefinition,
  TrashIcon,
} from '@vrplatform/icons';
import type { Maybe } from '@vrplatform/utils';
import {
  ActionIcon,
  Loader,
  Menu,
  type MenuItemProps,
  type MenuProps,
  Tooltip,
  useMantineTheme,
} from '@mantine/core';
import type { ReactNode } from 'react';
import { showSuccessNotification, useLegacyColors } from '@vrplatform/theme';
import type { ThemeColor } from '@vrplatform/theme';

type EllipsisMenuItemProps = Omit<MenuItemProps, 'icon'> & {
  icon?: IconDefinition;
  component?: any;
  to?: string;
  /** Anchor attributes for `component="a"`. */
  href?: string;
  target?: string;
  rel?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  iconColor?: string;
  customIcon?: ReactNode;
  loading?: boolean;
  loadingColor?: ThemeColor;
  tooltip?: string;
};

export const EllipsisMenuDivider = Menu.Divider;

export const EllipsisMenuItem = ({
  children,
  icon,
  iconColor,
  customIcon,
  loading,
  loadingColor,
  tooltip,
  ...props
}: EllipsisMenuItemProps) => {
  const content = (
    <Menu.Item
      leftSection={
        loading ? (
          <Loader color={loadingColor} size="xs" />
        ) : (
          customIcon ||
          (icon && <Icon icon={icon} color={iconColor} size={18} />)
        )
      }
      {...props}
    >
      {children}
    </Menu.Item>
  );

  return tooltip ? (
    <Tooltip label={tooltip} position="left" withArrow maw={400} multiline>
      {content}
    </Tooltip>
  ) : (
    content
  );
};

export const EllipsisMenuDangerItem = (props: EllipsisMenuItemProps) => {
  const { red } = useLegacyColors();

  return (
    <EllipsisMenuItem
      {...props}
      // icon={faTrashAlt}
      customIcon={
        <TrashIcon
          size={18}
          strokeWidth={1.5}
          color={props.iconColor || red.base}
        />
      }
      sx={{ color: red.base }}
      iconColor={props.iconColor || red.base}
      loadingColor="gray"
    />
  );
};

export const EllipsisMenuCopyItem = ({
  value,
  notification,
  ...props
}: EllipsisMenuItemProps & {
  value: Maybe<string>;
  notification?: string;
}) => {
  const theme = useMantineTheme();

  return (
    <EllipsisMenuItem
      customIcon={<CopyIcon size={16} color={theme.colors.neutral[5]} />}
      disabled={!value}
      onClick={() => {
        if (!value) return;

        navigator.clipboard.writeText(value).then(() =>
          showSuccessNotification({
            message: notification || 'ID was added to your clipboard.',
          })
        );
      }}
      {...props}
    >
      {props.children || 'Copy ID'}
    </EllipsisMenuItem>
  );
};

export const EllipsisMenuLabel = Menu.Label;

type Props = {
  children: ReactNode;
  icon?: IconDefinition;
  target?: ReactNode;
} & MenuProps;

export const EllipsisMenu = ({
  children,
  trigger = 'click',
  icon,
  target,
  ...menuProps
}: Props) => {
  const { gray, themeColors: colors } = useLegacyColors();

  return (
    <Menu
      shadow="md"
      width={200}
      radius="md"
      position="bottom-end"
      withArrow
      withinPortal
      trigger={trigger}
      arrowOffset={10}
      styles={() => ({
        item: {
          height: 35,
        },
        label: {
          color: gray.dark,
          textTransform: 'uppercase',
        },
      })}
      {...menuProps}
    >
      <Menu.Target>
        {target || (
          <ActionIcon
            disabled={menuProps.disabled}
            variant="subtle"
            color="gray"
            data-testid={menuProps['data-testid']}
          >
            <Icon
              icon={icon || 'MoreHorizontalIcon'}
              size={18}
              color={colors.neutral[4]}
            />
          </ActionIcon>
        )}
      </Menu.Target>
      <Menu.Dropdown>{children}</Menu.Dropdown>
    </Menu>
  );
};
